/**
 * Фабрика экземпляра keycloak-js, singleton для production и однократный `init` (check-sso).
 * По структуре соответствует passport-client `hooks/keycloak/keycloak.functions.ts`.
 *
 * @module hooks/keycloak/keycloak.functions
 */

import Keycloak from 'keycloak-js';
import { customize, KeycloakMock } from 'keycloak-js-mock';

import { getKeycloakBaseUrl, getKeycloakClientId, getKeycloakRealm } from '../../constants/keycloak';

import type { TKProfile } from './keycloak.types';

let keycloakSingleton: Keycloak.KeycloakInstance | null = null;

/**
 * Promise первичного init для singleton-экземпляра.
 * Предотвращает повторный вызов `keycloak.init()` при двойном запуске useEffect в React Strict Mode.
 */
let initCheckSsoPromise: Promise<boolean> | null = null;

const mockProfileBase: Omit<TKProfile, 'exp'> = {
  sub: 'userId123',
  is_profile_fulfill: true,
  email: 'm@mail.ru',
  locale: 'ru',
  preferred_username: 'Ann',
  given_name: 'Ann',
  family_name: 'Bystrova',
  name: 'Ann Bystrova',
};

/**
 * Создаёт singleton экземпляр Keycloak: мок или реальный клиент (как `keycloakInit` в passport-client).
 *
 * @param {boolean} mocked — использовать KeycloakMock для Storybook/тестов
 * @returns {Keycloak.KeycloakInstance}
 */
export function keycloakInit(mocked: boolean): Keycloak.KeycloakInstance {
  if (mocked) {
    customize.profile({ ...mockProfileBase });
    return new KeycloakMock();
  }

  if (keycloakSingleton === null) {
    keycloakSingleton = new Keycloak({
      url: getKeycloakBaseUrl(),
      realm: getKeycloakRealm(),
      clientId: getKeycloakClientId(),
    });
  }
  return keycloakSingleton;
}

/**
 * Инициализация с `onLoad: 'check-sso'` без hidden iframe-проверки.
 * Для реального singleton повторные вызовы (React Strict Mode) возвращают тот же Promise.
 *
 * @param {Keycloak.KeycloakInstance} kc — экземпляр из {@link keycloakInit}
 * @returns {Promise<boolean>} флаг `authenticated` после init
 */
export function initKeycloakCheckSso(kc: Keycloak.KeycloakInstance): Promise<boolean> {
  const isSingleton = kc === keycloakSingleton;
  if (isSingleton && initCheckSsoPromise !== null) {
    return initCheckSsoPromise;
  }

  const initPayload = {
    pkceMethod: 'S256' as const,
    onLoad: 'check-sso' as const,
    checkLoginIframe: false,
  };

  const promise = kc
    .init(initPayload)
    .then((authenticated: boolean) => authenticated)
    .catch((error: unknown) => {
      if (isSingleton) {
        initCheckSsoPromise = null;
      }
      throw error;
    });

  if (isSingleton) {
    initCheckSsoPromise = promise;
  }
  return promise;
}
